import { useState, useRef, useEffect } from 'react'
import { MessageSquare, X, Send, Sparkles, ChevronRight, Zap } from 'lucide-react'

export const AIChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [language, setLanguage] = useState('nl') // 'nl' or 'en'

    const messagesEndRef = useRef(null)

    // Initial Welcome Message based on language
    const getWelcomeMessage = (lang) => lang === 'nl'
        ? 'Hoi! Ik ben de ProPoule AI Assistent. 🤖 Waarmee kan ik je helpen?'
        : 'Hi! I am the ProPoule AI Assistant. 🤖 How can I help you?'

    const [messages, setMessages] = useState([
        { role: 'assistant', text: getWelcomeMessage('nl') }
    ])
    const [input, setInput] = useState('')
    const [isTyping, setIsTyping] = useState(false)

    // Switch Language Handler
    const toggleLanguage = () => {
        const newLang = language === 'nl' ? 'en' : 'nl'
        setLanguage(newLang)
        setMessages(prev => [...prev,
        { role: 'system', text: newLang === 'nl' ? 'Taal gewijzigd naar Nederlands 🇳🇱' : 'Language switched to English 🇬🇧' },
        { role: 'assistant', text: getWelcomeMessage(newLang) }
        ])
    }

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages, isOpen])

    // Knowledge Base (Multilingual)
    const knowledgeBase = {
        nl: [
            {
                keywords: ['maken', 'start', 'nieuw', 'begin', 'toernooi', 'creeer', 'poule', 'maak'],
                answer: "Om een nieuw toernooi of poule te maken: Ga naar het Dashboard en klik op de oranje knop 'Nieuw Toernooi'. Kies daarna je type (Poule, Knockout of Ladder)."
            },
            {
                keywords: ['scheids', 'login', 'pin', 'code', 'wachtwoord'],
                answer: "Scheidsrechters loggen in via de publieke link met een pincode. De standaard pincode is 0000. Je kunt deze wijzigen bij 'Toernooi Instellingen'."
            },
            {
                keywords: ['tv', 'scherm', 'presentatie', 'cast', 'monitor'],
                answer: "Voor de TV Modus: Ga naar 'Presentatie & TV' in het menu. Klik op 'Start Presentatie' en druk op F11 voor volledig scherm."
            },
            {
                keywords: ['stand', 'ranking', 'punten', 'score', 'tabel'],
                answer: "Standen worden automatisch berekend (3 pt winst, 1 pt gelijk). Je kunt dit helaas nog niet handmatig aanpassen."
            },
            {
                keywords: ['import', 'csv', 'excel', 'teams', 'spelers'],
                answer: "Ga naar 'Teams & Spelers' om via CSV teams te importeren. Er is een voorbeeldbestand beschikbaar."
            },
            {
                keywords: ['tennis', 'padel', 'set', 'game'],
                answer: "Ondersteuning voor sets (tennis/padel) staat op de roadmap voor versie 2.1. Nu ondersteunen we alleen scores."
            }
        ],
        en: [
            {
                keywords: ['create', 'start', 'new', 'begin', 'tournament', 'poule', 'make', 'add'],
                answer: "To create a new tournament: Go to the Dashboard and click the orange button 'New Tournament'. Choose your type (Poule, Knockout, or Ladder)."
            },
            {
                keywords: ['referee', 'login', 'pin', 'code', 'password'],
                answer: "Referees can login via the public link using a pin code. The default pin is 0000. You can change this in 'Tournament Settings'."
            },
            {
                keywords: ['tv', 'screen', 'presentation', 'cast', 'monitor'],
                answer: "For TV Mode: Go to 'Presentation & TV' in the menu. Click 'Start Presentation' and press F11 for full screen."
            },
            {
                keywords: ['standings', 'ranking', 'points', 'score', 'table'],
                answer: "Standings are calculated automatically (3pts win, 1pt draw). Manual adjustments are not yet supported."
            },
            {
                keywords: ['import', 'csv', 'excel', 'teams', 'players'],
                answer: "Go to 'Teams & Players' to import teams via CSV. A template file is available."
            },
            {
                keywords: ['tennis', 'padel', 'set', 'game'],
                answer: "Support for sets (tennis/padel) is on the roadmap for version 2.1. Currently, only goal-based scoring is supported."
            }
        ]
    }

    const handleSend = (e) => {
        e.preventDefault()
        if (!input.trim()) return

        const userMsg = input
        setMessages(prev => [...prev, { role: 'user', text: userMsg }])
        setInput('')
        setIsTyping(true)

        // AI Response Logic
        setTimeout(() => {
            const lowerQuery = userMsg.toLowerCase()
            const currentKB = knowledgeBase[language]

            const match = currentKB.find(item =>
                item.keywords.some(k => lowerQuery.includes(k))
            )

            let aiText = language === 'nl'
                ? "Hmm, dat weet ik niet precies. Probeer het anders te vragen of kijk op de Hulp pagina."
                : "Hmm, I'm not sure about that. Please rephrase or check the Help page."

            if (match) aiText = match.answer

            // Easter eggs
            if (lowerQuery.includes('hallo') || lowerQuery.includes('hoi') || lowerQuery.includes('hello') || lowerQuery.includes('hi')) {
                aiText = language === 'nl' ? "Hallo! 👋 Heb je een vraag?" : "Hello! 👋 Do you have a question?"
            }

            setMessages(prev => [...prev, { role: 'assistant', text: aiText }])
            setIsTyping(false)
        }, 1000)
    }

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">

            {/* Chat Window */}
            {isOpen && (
                <div className="mb-4 w-[350px] h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-10 fade-in duration-200">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 flex justify-between items-center text-white">
                        <div className="flex items-center gap-2">
                            <div className="bg-white/20 p-1.5 rounded-lg">
                                <Sparkles size={16} />
                            </div>
                            <span className="font-bold">AI Assistent</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={toggleLanguage} className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors font-bold uppercase" title="Switch Language">
                                {language === 'nl' ? '🇳🇱 NL' : '🇬🇧 EN'}
                            </button>
                            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded transition-colors">
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}>
                                {msg.role === 'system' ? (
                                    <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded-full">{msg.text}</span>
                                ) : (
                                    <div className={`
                                        max-w-[80%] p-3 rounded-2xl text-sm shadow-sm
                                        ${msg.role === 'user'
                                            ? 'bg-purple-600 text-white rounded-br-none'
                                            : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                                        }
                                    `}>
                                        {msg.text}
                                    </div>
                                )}
                            </div>
                        ))}
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-gray-100 p-3 rounded-2xl rounded-bl-none shadow-sm flex gap-1">
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <form onSubmit={handleSend} className="p-3 bg-white border-t border-gray-100 flex gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Stel je vraag..."
                            className="flex-1 bg-gray-100 border-0 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            autoFocus
                        />
                        <button
                            type="submit"
                            disabled={!input.trim()}
                            className="bg-purple-600 text-white p-2 rounded-xl hover:bg-purple-700 disabled:opacity-50 transition-colors"
                        >
                            <Send size={18} />
                        </button>
                    </form>
                </div>
            )}

            {/* Toggle Button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="group relative flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full shadow-lg hover:shadow-2xl hover:scale-110 transition-all duration-300"
                >
                    <div className="absolute inset-0 rounded-full bg-purple-400 opacity-0 group-hover:animate-ping opacity-20"></div>
                    <MessageSquare size={28} className="text-white" />

                    {/* Notification Dot */}
                    <span className="absolute top-0 right-0 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-white"></span>
                    </span>
                </button>
            )}
        </div>
    )
}
